import { AddOutlined, DeleteOutlined, EditOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { theme } from '@styles/theme';
import { useState } from 'react';

import { CARD, FIELD, PENCIL_BTN, SAVE_BTN, TITLE } from '../styles';

export interface ListItem { id: string; name: string }

export interface EditableListCardProps {
  title: string;
  items: ListItem[];
  loading?: boolean;
  addPending?: boolean;
  newValue: string;
  placeholder: string;
  infoText: string;
  leadingIcon?: React.ElementType;
  isAdmin?: boolean;
  onChange: (v: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
}

export const EditableListCard = ({
  title,
  items,
  loading,
  addPending,
  newValue,
  placeholder,
  infoText,
  leadingIcon: LeadingIcon,
  isAdmin = false,
  onChange,
  onAdd,
  onRemove,
}: EditableListCardProps) => {
  const [isEditingList, setIsEditingList] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<ListItem | null>(null);

  const canEdit = isAdmin && isEditingList;

  const handleConfirmRemove = () => {
    if (pendingRemove) onRemove(pendingRemove.id);
    setPendingRemove(null);
  };

  return (
  <Box sx={CARD}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '18px' }}>
      <Typography sx={TITLE}>{title}</Typography>
      {isAdmin && (
        <IconButton
          size="small"
          onClick={() => setIsEditingList((v) => !v)}
          sx={(theme) => ({ ...PENCIL_BTN, ...(isEditingList ? { color: theme.palette.primary.main, bgcolor: theme.palette.status.info.bg } : {}) })}
        >
          <EditOutlined sx={{ fontSize: 17 }} />
        </IconButton>
      )}
    </Box>

    {loading ? (
      <Box sx={{ py: '16px', display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} sx={(theme) => ({ color: theme.palette.primary.main })} />
      </Box>
    ) : (
      items.map((item, idx) => (
        <Box
          key={item.id}
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: '13px',
            borderBottom: idx < items.length - 1 ? `1px solid ${theme.palette.grey[100]}` : 'none',
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            {LeadingIcon && <LeadingIcon sx={{ fontSize: 16, color: theme.palette.grey[400] }} />}
            <Typography sx={(theme) => ({ fontSize: 14, color: theme.palette.grey[700], fontWeight: 500 })}>{item.name}</Typography>
          </Box>
          {canEdit && (
            <IconButton
              size="small"
              onClick={() => setPendingRemove(item)}
              sx={(theme) => ({
                width: 30, height: 30, borderRadius: '7px',
                bgcolor: '#fff1f0', color: theme.palette.error.main,
                '&:hover': { bgcolor: '#fee2e2' },
              })}
            >
              <DeleteOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      ))
    )}

    {/* Confirmation dialog */}
    <Dialog
      open={!!pendingRemove}
      onClose={() => setPendingRemove(null)}
      slotProps={{ paper: { sx: { borderRadius: '14px', p: '8px', width: 340, maxWidth: 'calc(100vw - 32px)' } } }}
    >
      <DialogTitle sx={(theme) => ({ fontSize: 15, fontWeight: 700, color: theme.palette.grey[900], pb: '6px' })}>
        Remove "{pendingRemove?.name}"?
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={(theme) => ({ fontSize: 13.5, color: theme.palette.grey[500] })}>
          This will permanently remove the entry. It will no longer appear in
          dropdowns or lists and cannot be recovered.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: '20px', pb: '16px', gap: '8px' }}>
        <Button
          onClick={() => setPendingRemove(null)}
          sx={(theme) => ({
            fontSize: 13, fontWeight: 600, textTransform: 'none',
            color: theme.palette.grey[700], borderRadius: '8px',
            '&:hover': { bgcolor: theme.palette.grey[100] },
          })}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmRemove}
          sx={(theme) => ({
            fontSize: 13, fontWeight: 600, textTransform: 'none',
            bgcolor: theme.palette.error.main, color: 'white', borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { bgcolor: theme.palette.status.error.text, boxShadow: 'none' },
          })}
          variant="contained"
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>

    {canEdit && (
      <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', mt: '16px', mb: '12px' }}>
        <TextField
          value={newValue}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onAdd()}
          placeholder={placeholder}
          size="small"
          fullWidth
          sx={FIELD}
          slotProps={{ htmlInput: { style: { paddingLeft: 12, paddingRight: 12 } } }}
        />
        <Button
          onClick={onAdd}
          disabled={addPending}
          startIcon={addPending ? <CircularProgress size={14} color="inherit" /> : <AddOutlined sx={{ fontSize: 16 }} />}
          sx={{ ...SAVE_BTN, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          Add
        </Button>
      </Box>
    )}

    <Typography sx={(theme) => ({ fontSize: 12.5, color: theme.palette.grey[400] })}>{infoText}</Typography>
  </Box>
  );
};
